import { Formik } from "formik";
import React, { FC } from "react";
import Modal from "react-modal";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CERTIFICATE_IMPORT_EXPORT_TYPES, CERTIFICATE_IS_CA, CERTIFICATE_STATUS } from "../../constants/certificate-constants";
import { CertificateAction } from "../../constants/certificateActions";
import { EncryptionTypes } from "../../constants/encryptionTypes";
import { MANAGE_CERT_GENERATE, MANAGE_CERT_IMPORT_PEM, MANAGE_CERT_IMPORT_PFX, MANAGE_CERT_REVOKE } from "../../constants/manage-certificates-types";
import { DEF_PG_SIZE_FILTER } from "../../constants/maximum-page-size";
import { useLocale } from "../../contexts/localeContext";
import { TableContextProvider } from "../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { downloadPfxFileFromBytes } from "../../exporters/ExportHelper";
import ActiveIcon from '../../images/circle-checked.svg';
import InActiveIcon from '../../images/Disabled.svg';
import { ButtonTypes } from "../../models/ButtonTypes";
import { CertificateDetailsType } from "../../models/CertificateType";
import certificatesColumns from "../../models/ColumnDefinitions/CertificatesColumns";
import { ListQuery } from "../../models/ListQuery";
import { MultiSelectOption } from "../../models/MultiSelectOption";
import { DeleteEntityResult } from "../../models/services/DeleteEntityResult";
import { TableColumn } from "../../models/table/TableColumn";
import { TableLegendItem } from "../../models/table/TableLegendItem";
import { exportCertificate, generateCertificate, getCertificateDetailsById, getCertificatesWithPagination, importMultipleCertificate, revokeCertificate } from "../../services/securityPolicyService";
import { getErrorMessage, showError } from "../../services/utilities/ServiceError";
import { IAppState } from "../../store/types";
import { buildCertForGeneration, handleImportCertificateSubmit } from "../../utilities/buildCertForGeneration";
import { appendFilter } from "../../utilities/filters";
import { convertTextPwdToBase64Pwd } from "../../utilities/Validation";
import AssignWizard from "../assignWizard/AssignWizard";
import { DialogContent } from "../dialog/Dialog";
import { useDialog } from "../dialog/DialogContext";
import DropdownSelector from "../dropdown-selector/DropdownSelector";
import CertificateDetails from "../security-policies/CertificateDetails";
import GenerateCertificateWizard from "../security-policies/Generate Certificate/GenerateCertificateWizard";
import GenerateConfirmCard from "../security-policies/GenerateConfirmModal";
import GenerateRecreateCertificateCard from "../security-policies/GenerateRecreateCertificateCard";
import { ImportExportCertificateCard } from "../security-policies/ImportExportCertificateCard";
import ImportPemCertificate from "../security-policies/ImportPemCertificate";
import TableTab from "../tabs/table-tab";

interface tabProps {
    dataMethod: any,
    columns: TableColumn[],
    searchDescriptors?: any,
    refreshMethod: any,
    lookup: any,
    getReportTitle: any,
    title: string,
    setBusy: React.Dispatch<React.SetStateAction<boolean>>,
    deleteMethod?: (id: string, isCustomDeleteKey?: boolean) => Promise<DeleteEntityResult>
}

type ModalType = "details" | "generateConfirm" | "generate" | "recreate" | "importPfx" | "export" | "importPem" | null;

export const CertificatesTab: FC<tabProps> = ({ dataMethod, columns, searchDescriptors, refreshMethod, lookup, getReportTitle, title, setBusy, deleteMethod }) => {
    const { translate } = useLocale();
    const user = useSelector((state: IAppState) => (state.userInfo));
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const [modalType, setModalType] = React.useState<ModalType>(null);
    const [certificateDetails, setCertificateDetails] = React.useState<CertificateDetailsType | undefined>();
    const [certToExportDetails, setCertToExportDetails] = React.useState<{ id: number, name: string }>({ id: 0, name: '' });
    const [recreateCertDetails, setRecreateCertDetails] = React.useState<any>();
    const [showGenerateCertificateWizard, setShowGenerateCertificateWizard] = React.useState(false);
    const [showRevokeCertificateWizard, setShowRevokeCertificateWizard] = React.useState(false);
    const uploadExportCertificateInitialValues = { certificatePassword: '' };
    const dialog = useDialog();
    const generateCertificateInitialValues = { id: 0, name: '', validFor: 1 };
    const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

    const manageCertificatesOptions: MultiSelectOption[] = [
        { value: MANAGE_CERT_IMPORT_PFX, label: translate.byKey(TranslationKeys.importPfx) },
        { value: MANAGE_CERT_IMPORT_PEM, label: translate.byKey(TranslationKeys.importPem) },
        { value: MANAGE_CERT_GENERATE, label: translate.byKey(TranslationKeys.generate) },
        { value: MANAGE_CERT_REVOKE, label: translate.byKey(TranslationKeys.revoke) }
    ];

    const handleRowClick = (e) => {
        setCertificateDetails(e);
        setModalType("details");
    };

    const handleExportCertificateSubmit = async (values) => {
        setBusy(true);
        try {
            let passwordBase64Pwd;
            if (values.certificateType === CERTIFICATE_IMPORT_EXPORT_TYPES.PFX) {
                passwordBase64Pwd = convertTextPwdToBase64Pwd(values.certificatePassword);
            }
            const certificateRaw = await exportCertificate(certToExportDetails.id, certToExportDetails.name, passwordBase64Pwd, values.certificateType, translate);
            const certificates = certificateRaw.data.attributes.files;
            certificates.map(certificate => {
                downloadPfxFileFromBytes(certificate.fileContentBytes, certificate.fileName);
            });
            setModalType(null);
        } catch (error) {
            showError(error, toast.warn, translate.byKey(TranslationKeys.certificateExportError));
        }
        setBusy(false);
    };

    const onGenerateCertificate = async (values) => {
        setBusy(true);
        try {
            const cert = buildCertForGeneration(values, 0, siteId, user.id);
            await generateCertificate(cert);
            setModalType(null);
        } catch (error) {
            showError(error, toast.warn, translate.byKey(TranslationKeys.certificateGenerationError));
        }
        setBusy(false);
    };

    const onRecreateCertificate = async (values) => {
        setBusy(true);
        try {
            const cert = buildCertForGeneration(values, values.id, siteId, user.id);
            await generateCertificate(cert);
            setModalType(null);
        } catch (error) {
            showError(error, toast.warn, translate.byKey(TranslationKeys.certificateRecreationError));
        }
        setBusy(false);
    };

    const onExportCertificate = (certificate: CertificateDetailsType) => {
        setCertToExportDetails({ id: certificate.id, name: certificate.name });
        setModalType("export");
    };

    const onBuildRecreateCertificateDetails = async (certificate: CertificateDetailsType) => {
        // If certificate is CA don't allow recreation
        if (certificate.isCaCertificate) {
            const dialogContent: DialogContent = {
                title: translate.byKey(TranslationKeys.generateCertificate),
                message: translate.byKey(TranslationKeys.recreateCAError),
            }

            dialog.pushError(dialogContent);
            return;
        }


        const san = certificate.subjectAlternativeName;
        const sanItems = san.split(',');
        const dnsList = sanItems.filter(entry => entry.startsWith('dns:')).join(',');
        const ipList = sanItems.filter(entry => entry.startsWith('ip:')).join(',');
        const uriList = sanItems.filter(entry => entry.startsWith('uri:')).join(',');
        const encryptionType = certificate.encryptionType?.startsWith("EC") ? EncryptionTypes.EC : EncryptionTypes.RSA;
        const keyParameter = encryptionType === EncryptionTypes.RSA ? certificate.keySize : certificate.curve;

        let caCertificateName = "";
        if (certificate.caCertificateId != null && !certificate.isCaCertificate) {
            const caCertificate = await getCertificateDetailsById(certificate.caCertificateId, translate);
            caCertificateName = caCertificate?.data ? (caCertificate.data["name"]) : "";
        }
        const fullCert = {
            id: certificate.id,
            alias: certificate.name,
            commonName: certificate.commonName,
            domainNameSystem: dnsList,
            internetProtocol: ipList,
            uniformResourceIdentifier: uriList,
            validFor: 0,
            encryptionType: encryptionType,
            shaAlgorithm: certificate.shaAlgorithm,
            shaAlgorithmLabel: certificate.shaAlgorithm || "",
            keyParameter: keyParameter,
            keyParameterLabel: keyParameter,
            certificateAuthorityId: certificate.caCertificateId,
            certificateAuthorityLabel: caCertificateName,
            certificateTypeId: certificate.certificateTypeId || 1,
            certificateTypeLabel: certificate.certificateTypeName,
            isCA: certificate.isCaCertificate
        }
        setRecreateCertDetails(fullCert);
        setModalType(null);
        setShowGenerateCertificateWizard(true);
    };

    const revokeSelectedCertificates = async () => {
        setBusy(true);
        const certIds = selectedIds.map(id => parseInt(id));
        if (certIds && certIds.length > 0) {
            // Actually revoke certificates
            try {
                await revokeCertificates(certIds)
            }
            catch (error) {
                showError(error, toast.warn, translate.byKey(TranslationKeys.certificateRecreationError));
            }
        }
        // do nothing
        setBusy(false);
        refreshMethod();
    }

    const revokeCertificates = (certificateIds: number[]) => {
        const dialogContent: DialogContent = {
            title: translate.byKey(TranslationKeys.revokeCertificate),
            message: translate.byKey(TranslationKeys.revokeCertContent),
            buttons: [
                {
                    id: "revokeCert",
                    text: translate.byKey(TranslationKeys.revoke),
                    onClick: () => {
                        revokeCertificateWithUpdateService(certificateIds, true);
                    },
                    type: ButtonTypes.RedRoundedButton
                },
                {
                    id: "cancelRevoke",
                    text: translate.byKey(TranslationKeys.cancel),
                    onClick: () => {
                    },
                    type: ButtonTypes.TextButton
                }
            ]
        }
        dialog.pushInfo(dialogContent);
    };

    const revokeCertificateWithUpdateService = async (certificateIds: number[], updateService: boolean) => {
        try {
            const response = await revokeCertificate(certificateIds, translate, siteId, updateService);
            if (response.success) {
                toast.success(translate.byKey(TranslationKeys.success));
            }
            else {
                toast.error(translate.byKey(TranslationKeys.error));
            }
        }
        catch (error) {
            showError(error, toast.warn, getErrorMessage(error));
        }
    }

    const onRevokeCertificate = (certificate: CertificateDetailsType) => {
        const ids:number[] = [certificate.id];
        // If certificate is CA don't allow revoking yet
        if (certificate.isCaCertificate) {
            const dialogContent: DialogContent = {
                title: translate.byKey(TranslationKeys.revokeCertificate),
                message: translate.byKey(TranslationKeys.revokeCAWarning)
            }
            dialog.pushError(dialogContent);
        }
        else {
            setBusy(true);
            setModalType(null);
            try {
                revokeCertificates(ids);
            }
            catch (error) {
                showError(error, toast.warn, getErrorMessage(error));
            }
            finally {
                setBusy(false);
                refreshMethod();
            }
        }
    };

    const onManageOptionClicked = (selectedOption) => {
        if (selectedOption.value === MANAGE_CERT_IMPORT_PFX) {
            setModalType("importPfx");
        } else if (selectedOption.value === MANAGE_CERT_GENERATE) {
            const dialogContent: DialogContent = {
                title: translate.byKey(TranslationKeys.generateCertificate),
                message: translate.byKey(TranslationKeys.generateCertificateWarningDescription),
                buttons: [
                    {
                        id: "generateCertificateId",
                        text: translate.byKey(TranslationKeys.generate),
                        onClick: () => {
                            setShowGenerateCertificateWizard(true);
                        },
                        type: ButtonTypes.BlueRoundedButton
                    },
                    {
                        id: "generateCertificateCancelId",
                        text: translate.byKey(TranslationKeys.cancel),
                        onClick: () => {},
                        type: ButtonTypes.TextButton
                    }
                ]
            }

            dialog.pushInfo(dialogContent);
        } else if (selectedOption.value === MANAGE_CERT_IMPORT_PEM) {
            setModalType("importPem");
        }
        else if (selectedOption.value === MANAGE_CERT_REVOKE) {
            const dialogContent: DialogContent = {
                title: translate.byKey(TranslationKeys.revokeCertificate),
                message: translate.byKey(TranslationKeys.revokeCertificateWarningDescription),
                buttons: [
                    {
                        id: "revokeCertificateId",
                        text: translate.byKey(TranslationKeys.revoke),
                        onClick: () => {
                            setShowRevokeCertificateWizard(true);
                        },
                        type: ButtonTypes.RedRoundedButton
                    },
                    {
                        id: "cancelRevokeCertificateId",
                        text: translate.byKey(TranslationKeys.cancel),
                        onClick: () => {
                            setShowRevokeCertificateWizard(false);
                        },
                        type: ButtonTypes.TextButton
                    }
                ]
            }

            dialog.pushInfo(dialogContent);
        }
    };

    const getCertificateTableLegendItems = (): TableLegendItem[] => [
        {
            value: "1",
            imageIcon: ActiveIcon,
            label: translate.byKey(TranslationKeys.validCertificate) ?? 'Active',
            tooltip: translate.byKey(TranslationKeys.certificateValidTooltip) ?? 'Active'
        },
        {
            value: "0",
            imageIcon: InActiveIcon,
            label: translate.byKey(TranslationKeys.expiredCertificate) ?? 'InActive',
            tooltip: translate.byKey(TranslationKeys.certificateExpiredTooltip) ?? 'InActive'
        }
    ];

    const importPEMCertificate = (values) => {
        const ca = {
            fileContentBytes: values.caCertificateFileContent,
            fileType:"pem",
            fileName: "ca"
        }

        const privateKey = {
            fileContentBytes: values.privateKeyFileContent,
            filePassword: values.password,
            fileType:"key",
            fileName: "key"
        }

        const certificate = {
            fileContentBytes: values.pemCertificateFileContent,
            fileType:"pem",
            fileName: "cert"
        }

        const allFiles = [ca, privateKey, certificate];
        importMultipleCertificate(values.alias, allFiles, translate, siteId, values.certificateType).then((response) => {
            toast.success(translate.byKey(TranslationKeys.success));
            setModalType(null);
        }).catch((error) => {
            showError(error, toast.warn, translate.byKey(TranslationKeys.certificateImportError));
        });
    }

    const getWizardContent = (modalType: ModalType) => {
        switch (modalType) {
            case "details":
                return (
                    <CertificateDetails
                        onClose={() => setModalType(null)}
                        onExportCertificate={onExportCertificate}
                        onRecreateCertificate={onBuildRecreateCertificateDetails}
                        onRevokeCertificate={onRevokeCertificate}
                        id={certificateDetails?.id} />
                );
            case "generateConfirm":
                return (
                    <GenerateConfirmCard
                        handleCancel={() => setModalType(null)}
                        handleConfirm={() => setModalType("generate")} />
                );
            case "generate":
                return (
                     <Formik initialValues={generateCertificateInitialValues} onSubmit={onGenerateCertificate}>
                         <GenerateRecreateCertificateCard
                            type={CertificateAction.GENERATE}
                            title={translate.byKey(TranslationKeys.generateCertificate)}
                            onClose={() => setModalType(null)} />
                     </Formik>
                );
            case "recreate":
                return (
                    <Formik initialValues={recreateCertDetails} onSubmit={onRecreateCertificate}>
                        <GenerateRecreateCertificateCard
                            type={CertificateAction.RECREATE}
                            title={translate.byKey(TranslationKeys.recreateCertificate)}
                            onClose={() => setModalType(null)} />
                    </Formik>
                );
            case "importPfx":
                return (
                    <Formik initialValues={uploadExportCertificateInitialValues} onSubmit={(values) => handleImportCertificateSubmit(values, setBusy, setModalType, toast.success, dialog, translate, siteId)}>
                        <ImportExportCertificateCard
                            type="import"
                            title={translate.byKey(TranslationKeys.importPfxCertificateTitle)}
                            onClose={() => setModalType(null)} />
                    </Formik>
                );
            case "export":
                return (
                    <Formik initialValues={uploadExportCertificateInitialValues} onSubmit={handleExportCertificateSubmit}>
                        <ImportExportCertificateCard
                            type="export"
                            title={translate.byKey(TranslationKeys.exportCertificate)}
                            onClose={() => setModalType(null)} />
                    </Formik>
                );
            case "importPem":
                return (
                    <Formik initialValues={{}} onSubmit={importPEMCertificate}>
                        <ImportPemCertificate onClose={() => setModalType(null)} />
                    </Formik>
                );
            default:
                return null;
        }
    }

    return (
        <>
            <div className="w-full flex flex-col">
                <div className="mb-3 w-full border-b border-gray-400" />
                <TableContextProvider
                    dataMethod={dataMethod}
                    deleteMethod={deleteMethod}
                    tableDescriptor={columns}
                    searchDescriptor={searchDescriptors}
                    useLegend
                    legendItems={getCertificateTableLegendItems()}
                    useTable
                    usePagination
                    useFilter
                    useSearch
                    useSort
                    manualDataRefresh={refreshMethod}
                    maxPageSize={DEF_PG_SIZE_FILTER}
                    lookup={lookup}>
                    <TableTab
                        title={title}
                        reportTitle={getReportTitle}
                        selectRows={false}
                        onRowClicked={handleRowClick}
                        cyName="certificates-table">
                        <DropdownSelector
                            className="flex px-4 rounded-full shadow text-white bg-sidemenu-focus text-sm items-center"
                            buttonClassName="flex px-8 items-center"
                            onSelected={onManageOptionClicked}
                            options={manageCertificatesOptions}>
                            {translate.byKey(TranslationKeys.manage)}
                        </DropdownSelector>
                    </TableTab>
                </TableContextProvider>
            </div>
            <Modal
                isOpen={modalType !== null}
                onRequestClose={() => setModalType(null)}
                ariaHideApp={false}
                overlayClassName="flex w-screen position-float-modal fixed top-0 left-0 h-screen bg-opacity-50 bg-black"
                className="bg-white border-none m-auto shadow select-none outline-none w-content">
                {getWizardContent(modalType)}
            </Modal>
            <GenerateCertificateWizard
                isOpen={showGenerateCertificateWizard}
                setIsOpen={setShowGenerateCertificateWizard}
                setBusy={setBusy}
                certificateDetails={recreateCertDetails}/>
            <AssignWizard
                isOpen={showRevokeCertificateWizard}
                dataMethod={(listParameters?: ListQuery) => getCertificatesWithPagination(translate, {...listParameters,
                        filters: appendFilter(CERTIFICATE_IS_CA.NO, "Certificate CA", "certificateAuthority",
                            appendFilter(CERTIFICATE_STATUS.REVOKED, "Certificate Status", "ksCertificateStatus.id", [], "=out=",),),
                    })
                }
                lookup={lookup}
                onClose={() => {setShowRevokeCertificateWizard(false);}}
                title={translate.byKey(TranslationKeys.revokeCertificate)}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                alwaysSelectedAndDisabled={undefined}
                columnDescriptors={{columns: certificatesColumns, searchColumns: searchDescriptors,}}
                handleOnSubmit={revokeSelectedCertificates}/>
        </>
    );
};
